/**
 * Utilities for fetching and managing user sites via the app-switcher API
 */

export type Site = {
	cloudId: string;
	displayName: string;
	url: string;
	avatar?: string;
	org?: {
		id: string;
		displayName: string;
	};
	availableProducts?: Array<{
		productType: string;
		url?: string;
		activationDate?: string;
	}>;
	adminAccess?: boolean;
};

export type SitesResponse = {
	startUrl?: string;
	isPartial: boolean;
	sites: Site[];
};

/**
 * Extracts the base URL from a site (removing any path or query params)
 * @param siteUrl - Full URL from app-switcher (may include login redirects)
 * @returns string - Clean base URL
 */
export function extractSiteBaseUrl(siteUrl: string): string {
	try {
		// The app-switcher URLs often have format:
		// https://id.atlassian.com/login?...&continue=https%3A%2F%2Fhello.atlassian.net
		// https://id.stg.internal.atlassian.com/login?...&continue=https%3A%2F%2Fnlahoti-test.jira-dev.com
		// We need to extract the actual site URL from the continue parameter

		const url = new URL(siteUrl);

		// Check if this is a login redirect URL (production or staging)
		const isIdService =
			url.hostname === 'id.atlassian.com' ||
			url.hostname === 'id.stg.internal.atlassian.com' ||
			(url.hostname.includes('id.') && url.hostname.includes('atlassian'));

		if (isIdService && url.pathname === '/login') {
			const continueParam = url.searchParams.get('continue');
			if (continueParam) {
				// Decode and extract the base URL
				const decodedUrl = decodeURIComponent(continueParam);
				const continueUrl = new URL(decodedUrl);
				const extractedUrl = `${continueUrl.protocol}//${continueUrl.hostname}`;
				return extractedUrl;
			}
		}

		// Otherwise just return the base URL
		const baseUrl = `${url.protocol}//${url.hostname}`;
		return baseUrl;
	} catch (error) {
		console.error('[extractSiteBaseUrl] Error parsing site URL:', error);
		return siteUrl;
	}
}

/**
 * Converts a site to a format suitable for display in a select dropdown
 */
export type SiteOption = {
	value: string; // cloudId
	label: string; // displayName
	url: string; // base URL
	org?: string; // org displayName
	ari?: string; // Atlassian Resource Identifier
};

export function siteToOption(site: Site): SiteOption {
	return {
		value: site.cloudId,
		label: site.displayName,
		url: extractSiteBaseUrl(site.url),
		org: site.org?.displayName,
		ari: buildSiteAri(site.cloudId),
	};
}

/**
 * Builds an Atlassian Resource Identifier (ARI) for a site
 * @param cloudId - The site's cloud ID
 * @returns string - ARI in format: ari:cloud:platform::site/{cloudId}
 */
export function buildSiteAri(cloudId: string): string {
	return `ari:cloud:platform::site/${cloudId}`;
}

/**
 * Extracts cloud ID from an ARI
 * @param ari - ARI string
 * @returns string | null - Cloud ID if valid ARI, null otherwise
 */
export function extractCloudIdFromAri(ari: string): string | null {
	const match = ari.match(/^ari:cloud:platform::site\/(.+)$/);
	return match ? match[1] : null;
}
